// lib/api.ts
import axios, {
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { getCookie, deleteCookie } from "cookies-next";
import { toast } from "react-toastify";

// Utility function to extract path from URL
const extractPath = (url: string | undefined): string => {
  if (!url) return "unknown";
  // Remove query parameters and hash, keep only the path
  const path = url.split("?")[0].split("#")[0];
  return path || "unknown";
};

/** Same cookie order as `globalLegalPolicyApi` / common dashboard calls. */
function readLoggedInCompanyUserIdForApi(): string {
  const fromCookies =
    getCookie("company_user_id") ??
    getCookie("logged_in_company_user_id") ??
    getCookie("ca_user_id");
  if (fromCookies != null && String(fromCookies).trim() !== "") {
    return String(fromCookies);
  }
  const token = getCookie("token");
  if (typeof token !== "string" || !token) return "";
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return "";
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json) as Record<string, unknown>;
    const id = payload.company_user_id;
    if (id != null && String(id).trim() !== "") return String(id);
  } catch {
    return "";
  }
  return "";
}

function ensureApReportLoggedInCompanyUserQueryParam(
  config: InternalAxiosRequestConfig,
): void {
  const path = extractPath(config.url);
  if (!path.includes("/v1/ap_report/")) return;
  const method = (config.method ?? "get").toLowerCase();
  if (method !== "get" && method !== "delete") return;

  const loggedIn = readLoggedInCompanyUserIdForApi();
  if (!loggedIn) return;

  const prev = config.params;
  const base: Record<string, unknown> =
    prev && typeof prev === "object" && !Array.isArray(prev)
      ? { ...(prev as Record<string, unknown>) }
      : {};
  const current = String(base.p_logged_in_company_user_id ?? "").trim();
  if (!current) {
    base.p_logged_in_company_user_id = loggedIn;
    config.params = base;
  }
}

// Utility function to get user IP address
async function getUserIpAddress(): Promise<string> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    const ipResponse = await fetch("https://api.ipify.org?format=json", {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (ipResponse.ok) {
      const ipData = await ipResponse.json();
      return ipData.ip || "Unknown";
    }
  } catch (ipError: any) {
    if (ipError.name !== "AbortError") {
      console.warn("Failed to fetch IP address:", ipError);
    }
  }
  return "Unknown";
}

// Create Axios instance
console.log("API URL being used:", process.env.NEXT_PUBLIC_API_URL);
const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Helper function to dismiss toasts while preserving push notification toasts
const dismissToastsPreservingPushNotifications = () => {
  // Check if there are any active push notification toasts
  const pushNotificationToasts =
    typeof window !== "undefined" && (window as any).__pushNotificationToastIds
      ? ((window as any).__pushNotificationToastIds as Set<string>)
      : new Set<string>();

  // If there are active push notification toasts, skip dismissing toasts entirely
  // to prevent push notification toasts from being dismissed
  if (pushNotificationToasts.size > 0) {
    console.log(
      "[API Interceptor] Skipping toast dismissal - push notification toasts are active",
    );
    return;
  }

  // Only dismiss toasts if there are no push notification toasts
  toast.dismiss();
};

// Request interceptor for auth headers
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Skip dismissing toasts for push notification related API calls
    // to prevent push notification toasts from being dismissed immediately after display
    const isAuditApi = config.url?.includes("/v1/audit");
    const isPushNotificationApi =
      isAuditApi &&
      (typeof config.data === "string"
        ? config.data.includes("push_notification")
        : JSON.stringify(config.data || {}).includes("push_notification"));

    // Also skip for notification-related endpoints
    const isNotificationEndpoint =
      config.url?.includes("notification") || config.url?.includes("firebase");

    if (!isPushNotificationApi && !isNotificationEndpoint) {
      // Only dismiss toasts for non-push-notification API calls
      dismissToastsPreservingPushNotifications();
    }

    const token = getCookie("token"); // Adjust based on your auth setup
    if (token && config.headers) {
      config.headers.Token = `${token}`;
    }
    ensureApReportLoggedInCompanyUserQueryParam(config);
    return config;
  },
  (error: AxiosError) => {
    // Handle request setup errors (e.g., invalid config)
    return Promise.reject({
      message: "Request configuration failed",
      details: error.message,
    });
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Return successful responses directly
    return response;
  },
  (error: AxiosError) => {
    // Extract response data and status
    const { response, request, config } = error;
    let errorMessage =
      "We are very sorry for the inconvenience. The app encountered an error. We have informed the administrator. Please try again later.";
    const statusCode = response?.status;

    const token = getCookie("token");

    const requestUrl = config?.url || "unknown";
    const baseUrl = config?.baseURL || "unknown";
    const requestPath = extractPath(config?.url); // Capture request path

    // Create a custom error object
    const customError = {
      message: errorMessage,
      status: statusCode,
      details: response?.data || error.message,
    };

    const details = {
      message: "",
      error_message: "",
    };

    if (response) {
      // Server responded with a status code
      if (statusCode == 401) {
        // Call logout API before clearing cookies
        logoutUser().catch((error) => {
          console.error("Error calling logout API in 401 handler:", error);
          // Continue with logout even if API call fails
        });

        // Optional: Trigger logout or token refresh
        document.cookie =
          "isAuthenticated=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        deleteCookie("token");
        deleteCookie("ca_user_id");
        deleteCookie("company_id");
        deleteCookie("company_user_id");
        deleteCookie("user_type");
        deleteCookie("user_type_id");
        deleteCookie("user_pay_type");
        deleteCookie("user_role");
        deleteCookie("user_role_id");
        deleteCookie("user_full_name");
        deleteCookie("first_name");
        deleteCookie("middle_name");
        deleteCookie("last_name");
        deleteCookie("password_expiry_dtm");
        deleteCookie("locked_out_flag");
        deleteCookie("email");
        deleteCookie("ca_user_active_flag");
        deleteCookie("company_user_active_flag");
        deleteCookie("user_company_association_flag");
        deleteCookie("sns_id");
        deleteCookie("message");
        deleteCookie("status");
        deleteCookie("session_timeout_minutes");

        window.location.href = "/login";
      }

      if (statusCode == 400 || statusCode == 404 || statusCode == 500) {
        const skipErrorReporting =
          requestPath === "/v1/care_ascendium_error/new" ||
          requestPath === "/v1/ca_support/new";
        if (!skipErrorReporting) {
          console.log("Error Block: " + response.data);

          // Get current page path
          const currentPage =
            typeof window !== "undefined"
              ? window.location.pathname
              : "unknown";

          // Get IP address and append to error description
          getUserIpAddress().then((ipAddress) => {
            let errorDesc =
              (response.data as any)?.message?.replace(/'/g, '"') ||
              (response.data as any)?.error?.replace(/'/g, '"') ||
              "Unknown error";
            errorDesc += `\n\nIP Address: ${ipAddress}`;

            api
              .post("/v1/care_ascendium_error/new", {
                p_request_path: requestPath,
                p_current_page: currentPage,
                p_ca_app: "Web",
                p_error_desc: errorDesc,
                p_ca_user_id: getCookie("ca_user_id"),
                p_ca_user_email: getCookie("email"),
                p_device_info: navigator.userAgent,
              })
              .then(() => {
                import("@/components/SupportTicket/api")
                  .then(({ createSupportTicketForAppError }) => {
                    createSupportTicketForAppError({
                      subject: `[App error] ${requestPath} (${statusCode})`,
                      question: `Automatic ticket from web app error.\n\nPage: ${currentPage}\nAPI: ${requestPath}\nStatus: ${statusCode}\n\n${errorDesc}`,
                    });
                  })
                  .catch(() => {});
                toast.error(
                  "We are very sorry for the inconvenience. The app encountered an error. We have informed the administrator. Please try again later.",
                  {
                    toastId: "error1",
                    autoClose: 5000,
                  },
                );
              });
          });
        }
      }

      if (statusCode == 403) {
        const skipErrorReporting403 =
          requestPath === "/v1/care_ascendium_error/new" ||
          requestPath === "/v1/new";
        if (!skipErrorReporting403) {
          console.log("Error Block: " + response.data);

          // Extract server error message if available
          const serverErrorMessage =
            (response.data as any)?.message ||
            (response.data as any)?.error ||
            (response.data as any)?.detail ||
            JSON.stringify(response.data) ||
            "Permission denied";

          // Check if this might be a WAF block (common WAF indicators)
          const responseString = JSON.stringify(
            response.data || {},
          ).toLowerCase();
          const responseHeaders = response.headers || {};
          const isWAFBlock =
            responseString.includes("waf") ||
            responseString.includes("web application firewall") ||
            responseString.includes("blocked") ||
            responseString.includes("forbidden by security policy") ||
            responseString.includes("security violation") ||
            responseHeaders["x-waf-blocked"] ||
            responseHeaders["cf-ray"] || // Cloudflare
            responseHeaders["x-aws-waf"] ||
            responseHeaders["x-azure-waf"];

          // Get user information for better context
          const caUserId = getCookie("ca_user_id");
          const userEmail = getCookie("email");
          const userFullName = getCookie("user_full_name");
          const httpMethod = config?.method?.toUpperCase() || "UNKNOWN";

          // Safely extract payload information (limit size and sanitize)
          let payloadInfo = "N/A";
          try {
            if (config?.data) {
              const payloadStr =
                typeof config.data === "string"
                  ? config.data
                  : JSON.stringify(config.data);
              // Limit payload size to first 500 characters to avoid huge error messages
              const truncatedPayload =
                payloadStr.length > 500
                  ? payloadStr.substring(0, 500) + "... [truncated]"
                  : payloadStr;
              // Sanitize single quotes for database storage
              payloadInfo = truncatedPayload.replace(/'/g, '"');
            }
          } catch (e) {
            payloadInfo = "Unable to extract payload";
          }

          // Get request headers (excluding sensitive ones)
          let headersInfo = "N/A";
          try {
            const safeHeaders: Record<string, string> = {};
            if (config?.headers) {
              Object.keys(config.headers).forEach((key) => {
                // Exclude sensitive headers
                if (
                  !key.toLowerCase().includes("token") &&
                  !key.toLowerCase().includes("authorization") &&
                  !key.toLowerCase().includes("cookie")
                ) {
                  safeHeaders[key] = String(config.headers[key]);
                }
              });
              headersInfo = JSON.stringify(safeHeaders).replace(/'/g, '"');
            }
          } catch (e) {
            headersInfo = "Unable to extract headers";
          }

          // Get current page path
          const currentPage =
            typeof window !== "undefined"
              ? window.location.pathname
              : "unknown";

          // Create detailed error message for administrators
          const errorType = isWAFBlock ? "WAF Block" : "Permission Denied";
          let detailedError =
            `403 Forbidden - ${errorType}. ` +
            `User: ${userFullName || "Unknown"} (ID: ${caUserId || "N/A"}, Email: ${userEmail || "N/A"}) ` +
            `attempted to access ${httpMethod} ${requestPath}. ` +
            `Server response: ${serverErrorMessage.replace(/'/g, '"')}. ` +
            `${isWAFBlock ? "WAF may have blocked this request based on payload content or request pattern. " : "Please review user permissions and role assignments. "}` +
            `Request payload: ${payloadInfo}. ` +
            `Request headers: ${headersInfo}. ` +
            `User Agent: ${navigator.userAgent || "N/A"}. ` +
            `Timestamp: ${new Date().toISOString()}.`;

          // Get IP address and append to error description
          getUserIpAddress().then((ipAddress) => {
            detailedError += `\n\nIP Address: ${ipAddress}`;

            api
              .post("/v1/care_ascendium_error/new", {
                p_request_path: requestPath,
                p_current_page: currentPage,
                p_ca_app: "Web",
                p_error_desc: detailedError,
                p_ca_user_id: caUserId,
                p_ca_user_email: userEmail,
                p_device_info: navigator.userAgent,
              })
              .then(() => {
                import("@/components/SupportTicket/api")
                  .then(({ createSupportTicketForAppError }) => {
                    createSupportTicketForAppError({
                      subject: `[App error] 403 ${requestPath}`,
                      question: `Automatic ticket (403 / permission or WAF).\n\nPage: ${currentPage}\nAPI: ${requestPath}\n\n${detailedError}`,
                    });
                  })
                  .catch(() => {});
                toast.error(
                  "We are very sorry for the inconvenience. The app encountered an error. We have informed the administrator. Please try again later.",
                  {
                    toastId: "error1",
                    autoClose: 5000,
                  },
                );
              });
          });
        }
      }
    } else if (request) {
      // Request was made but no response received (e.g., network error)
      errorMessage =
        "No internet connection. Please check your network and try again.";

      console.log(customError);

      details.error_message = errorMessage;
      details.message = errorMessage;

      customError.details = details;
    } else {
      // Error setting up the request
      errorMessage = `Request Error: ${error.message}`;
    }

    customError.message = errorMessage;

    // Log error for debugging (optional)
    console.log("API Error:", customError);

    // Reject with custom error
    const normalizedError = new Error(
      typeof customError.details === "string"
        ? customError.details
        : JSON.stringify(customError.details)
    );
    
    // attach extra info
    (normalizedError as any).status = customError.status;
    (normalizedError as any).details = customError.details;
    
    return Promise.reject(normalizedError);
  },
);

export const isCancel = axios.isCancel;
export default api;

// Audit API functions
import { getCombinedInfo } from "../utils/browserUtils";

export interface AuditData {
  company_id?: string | number;
  ca_user_id?: string | number;
  company_user_id?: string | number;
  authentication_token?: string;
  ui_app?: string;
  device_or_browser?: string;
  os?: string;
  version?: string;
  page?: string;
  page_status?: string;
  sp?: string;
  action?: string;
  audit_message?: string;
  logged_in_company_user_id?: string | number;
}

export async function createAudit(auditData: AuditData) {
  try {
    // Get browser and app information
    const { browser, app } = getCombinedInfo();

    // Prepare the request payload with the specific fields
    const payload = {
      p_company_id: auditData.company_id || getCookie("company_id"),
      p_ca_user_id: auditData.ca_user_id || getCookie("ca_user_id"),
      p_company_user_id:
        auditData.company_user_id || getCookie("company_user_id"),
      p_authentication_token:
        auditData.authentication_token || getCookie("token"),
      p_ui_app: "Web App",
      p_device_or_browser: auditData.device_or_browser || browser.browser,
      p_os: auditData.os || browser.os,
      p_version: auditData.version || browser.browserVersion,
      p_page: auditData.page || app.currentUrl,
      p_page_status: auditData.page_status || "active",
      p_sp: auditData.sp || "Not Provided",
      p_action: auditData.action || "audit_log",
      p_audit_message: auditData.audit_message || "Audit event logged",
      p_logged_in_company_user_id:
        auditData.logged_in_company_user_id || getCookie("company_user_id"),
    };

    const response = await api.post("/v1/ca_audit/new", payload);
    return response.data;
  } catch (error) {
    console.error("Error creating audit:", error);
    throw error;
  }
}

// Convenience function for simple audit logging
export async function logAuditEvent(
  action: string,
  auditMessage: string,
  page?: string,
  additionalData?: Partial<AuditData>,
) {
  return createAudit({
    action,
    audit_message: auditMessage,
    page,
    ...additionalData,
  });
}

// Logout API function
export async function logoutUser() {
  try {
    const token = getCookie("token");
    const caUserId = getCookie("ca_user_id");
    const companyId = getCookie("company_id");
    const companyUserId = getCookie("company_user_id");

    if (token && caUserId && companyId && companyUserId) {
      await api.post("/v1/user_authentication/user_logout", {
        p_ca_user_id: caUserId,
        p_company_id: companyId,
        p_company_user_id: companyUserId,
        p_authentication_token: token,
      });
    }
  } catch (error) {
    console.error("Error calling logout API:", error);
    // Don't throw error - logout should proceed even if API call fails
  }
}
